import {
  DeviceSizeMap,
  DeviceSizeMapType,
  DeviceSizeOrder,
} from 'beek-design-system/defaults/DeviceSizes';
import fallbacks from './imageFallbacks';

const {
  audioCoverFallback,
  articlePageCoverFallback,
  articleCoverFallback,
  error404Fallback,
} = fallbacks;

export type ImageDimensions = {
  width: number;
  height: number;
};

export type ImageConfigSizeType = {
  sizes: {
    [key_size: string]: ImageDimensions;
  };
  fallbackImage: string;
};

export type ImageConfigSizeDict = {
  [key: string]: ImageConfigSizeType;
};

export type UrlConfigSize = {
  [key_size: string]: string;
};

export const typeConfigs: ImageConfigSizeDict = {
  default: {
    sizes: {
      mobileS: {
        width: 50,
        height: 50,
      },
      laptop: {
        width: 100,
        height: 100,
      },
    },
    fallbackImage: audioCoverFallback,
  },
  audio_cover: {
    sizes: {
      mobileS: {
        width: 96,
        height: 96,
      },
      laptop: {
        width: 216,
        height: 216,
      },
    },
    fallbackImage: audioCoverFallback,
  },
  audio_card_cover: {
    sizes: {
      mobileS: {
        width: 168,
        height: 168,
      },
      laptop: {
        width: 256,
        height: 256,
      },
    },
    fallbackImage: audioCoverFallback,
  },
  article_page_cover: {
    sizes: {
      mobileS: {
        width: 288,
        height: 180,
      },
      mobileM: {
        width: 343,
        height: 199,
      },
      mobileL: {
        width: 382,
        height: 223,
      },
      laptop: {
        width: 372,
        height: 372,
      },
    },
    fallbackImage: articlePageCoverFallback,
  },
  article_cover: {
    sizes: {
      mobileS: {
        width: 343,
        height: 191,
      },
      laptop: {
        width: 288,
        height: 160,
      },
    },
    fallbackImage: articleCoverFallback,
  },
  ad: {
    sizes: {
      mobileS: {
        width: 279,
        height: 265,
      },
      tablet: {
        width: 395,
        height: 372,
      },
    },
    fallbackImage: articleCoverFallback,
  },
  popular_articles: {
    sizes: {
      mobileS: {
        width: 288,
        height: 160,
      },
      laptop: {
        width: 217,
        height: 122,
      },
    },
    fallbackImage: articleCoverFallback,
  },
  related_articles: {
    sizes: {
      mobileS: {
        width: 288,
        height: 168,
      },
      mobileM: {
        width: 343,
        height: 199,
      },
      mobileL: {
        width: 382,
        height: 223,
      },
      laptop: {
        width: 288,
        height: 160,
      },
    },
    fallbackImage: articleCoverFallback,
  },
  more_to_learn: {
    sizes: {
      mobileS: {
        width: 140,
        height: 140,
      },
      laptop: {
        width: 140,
        height: 140,
      },
    },
    fallbackImage: audioCoverFallback,
  },
  mosaic: {
    sizes: {
      mobileS: {
        width: 320,
        height: 332,
      },
      mobileM: {
        width: 375,
        height: 389,
      },
      mobileL: {
        width: 425,
        height: 441,
      },
      laptop: {
        width: 466,
        height: 484,
      },
    },
    fallbackImage: articlePageCoverFallback,
  },
  discount: {
    sizes: {
      mobileS: {
        width: 159,
        height: 20,
      },
      laptop: {
        width: 159,
        height: 25,
      },
    },
    fallbackImage: audioCoverFallback,
  },
  headphones: {
    sizes: {
      mobileS: {
        width: 200,
        height: 218.28,
      },
      laptop: {
        width: 425,
        height: 429,
      },
    },
    fallbackImage: audioCoverFallback,
  },
  covers_grid: {
    sizes: {
      mobileS: {
        width: 320,
        height: 330,
      },
      mobileM: {
        width: 381,
        height: 393,
      },
      laptop: {
        width: 1021,
        height: 240,
      },
    },
    fallbackImage: audioCoverFallback,
  },
  devices: {
    sizes: {
      mobileS: {
        width: 268,
        height: 362,
      },
      laptop: {
        width: 350,
        height: 388,
      },
    },
    fallbackImage: audioCoverFallback,
  },
  ad_home: {
    sizes: {
      mobileS: {
        width: 294,
        height: 370,
      },
      mobileM: {
        width: 294,
        height: 370,
      },
      mobileL: {
        width: 294,
        height: 370,
      },
      laptop: {
        width: 360,
        height: 460,
      },
    },
    fallbackImage: articlePageCoverFallback,
  },
  search: {
    sizes: {
      mobileS: {
        width: 40,
        height: 40,
      },
      laptop: {
        width: 80,
        height: 80,
      },
    },
    fallbackImage: audioCoverFallback,
  },
  mail_blank: {
    sizes: {
      mobileS: {
        width: 200,
        height: 200,
      },
      mobileM: {
        width: 200,
        height: 200,
      },
      mobileL: {
        width: 200,
        height: 200,
      },
      laptop: {
        width: 222,
        height: 222,
      },
    },
    fallbackImage: audioCoverFallback,
  },
  not_found: {
    sizes: {
      mobileS: {
        width: 264,
        height: 214,
      },
      laptop: {
        width: 294,
        height: 245,
      },
    },
    fallbackImage: error404Fallback,
  },
};

export const getConfigSizeFromType = (type: string): ImageConfigSizeType =>
  typeConfigs[type];

export const constructWidthSizeMap = (
  configSize: ImageConfigSizeType
): DeviceSizeMapType => {
  const sizeMap: DeviceSizeMapType = {};

  DeviceSizeOrder.forEach((sizeKey) => {
    if (configSize.sizes[sizeKey]) {
      sizeMap[sizeKey] = DeviceSizeMap[sizeKey];
    }
  });

  return sizeMap;
};

export default typeConfigs;
